import React, { ComponentType, CSSProperties, forwardRef } from 'react';
import * as S from './styles';
import { IconProps } from '@/types/icon';
import { DefaultTheme, useTheme } from 'styled-components';
import Badge from '../Badge';
import Text from '../Text';
import Spinner from '../Spinner';

export type IconButtonSize = 1 | 2 | 3 | 4;
export type IconButtonState = 'default' | 'disable';
export type IconButtonVariant =
  | 'solid'
  | 'soft'
  | 'surface'
  | 'outline'
  | 'ghost';
export type IconButtonColor =
  | 'accent'
  | 'neutral'
  | 'error'
  | 'success'
  | 'warning'
  | 'info';
export type IconButtonShape = 'round' | 'rectangular';

export type IconButtonProps = {
  $size: IconButtonSize;
  $shape?: IconButtonShape;
  $state: IconButtonState;
  $variant: IconButtonVariant;
  $color: IconButtonColor;
  $highContrast: boolean;
  $loading: boolean;
  $Icon: ComponentType<IconProps>;
  $iconProps?: object;
  $focusRing: boolean;
  style?: CSSProperties;
  $onClick: React.MouseEventHandler<HTMLButtonElement>;
  $textoBadge?: string;
  $textoInferior?: string;
  $disabled?: boolean;
  $mostrarEstilizacaoHover?: boolean;
};

export function getCorIcone({
  theme,
  $variant,
  $state,
  $color,
  $highContrast,
}: {
  theme: DefaultTheme;
  $state: IconButtonState;
  $variant: string;
  $color: string;
  $highContrast: boolean;
}) {
  if (['ghost', 'surface', 'soft', 'outline'].includes($variant))
    return theme.colors[$color][$highContrast ? $color : `${$color}_alpha`][
      $highContrast ? '12' : '11'
    ];
  if ($state === 'disable') return theme.colors[$color][`${$color}_alpha`]['8'];
  const constrast = $highContrast ? 'surface' : 'contrast';
  return theme.tokens.colors[`${$color.toLowerCase()}_${constrast}`];
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (props, ref) => {
    const {
      style,
      $Icon,
      $size,
      $state,
      $color,
      $variant,
      $highContrast,
      $iconProps = {},
      $onClick,
      $textoBadge,
      $textoInferior,
      $disabled = false,
      $mostrarEstilizacaoHover = true,
      $shape = 'rectangular',
      $loading,
    } = props;

    const containerProps = {
      $highContrast,
      $color,
      $size,
      $variant,
      $state,
      $disabled,
      $mostrarEstilizacaoHover,
    };

    const tamanhoIcone = $size + 4;
    const theme = useTheme();

    return (
      <S.ContainerTotal>
        <S.Container
          ref={ref}
          style={style}
          onClick={$onClick}
          disabled={$disabled}
          type="button"
          $shape={$shape}
          {...containerProps}
        >
          {$textoBadge ? (
            <S.ContainerBadge>
              <Badge
                $size={1}
                $rounded={true}
                $variant="solid"
                $color="success"
                $highContrast={true}
                $text={$textoBadge}
              />
            </S.ContainerBadge>
          ) : null}
          {$loading ? (
            <Spinner
              tamanho={
                theme.typography.font.size[
                  tamanhoIcone >= 10 ? 10 : tamanhoIcone
                ]
              }
              corPrincipal={theme.colors.accent.accent[7]}
            />
          ) : (
            <$Icon
              color={getCorIcone({
                $color,
                $state,
                $variant,
                theme,
                $highContrast,
              })}
              size={
                theme.typography.font.size[
                  tamanhoIcone >= 10 ? 10 : tamanhoIcone
                ]
              }
              {...$iconProps}
            />
          )}
        </S.Container>
        {$textoInferior ? (
          <Text
            $size={1}
            $color="neutral"
            $weight="regular"
            $align="center"
            $highContrast={true}
            $text={$textoInferior}
            style={{ paddingTop: theme.spacing[3], maxWidth: 70 }}
          />
        ) : null}
      </S.ContainerTotal>
    );
  },
);

IconButton.displayName = 'IconButton';

export default IconButton;
